import React, { useState, useEffect } from 'react';

import { List, Card, notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { useProjectContext, useProjectContextDispatch } from '@contexts/ProjectContext';

import { forceReloadProject } from '@hooks/API/Project/UseProject';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import EmptyState from '@components/Gui/EmptyState';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwAddButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDrawer from '@components/Gui/NwDrawer';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import MediaDrawerPreviewOnly from '@components/MediaDrawer/MediaDrawerPreviewOnly';
import PdfPrintDrawer from '@components/PdfPrintDrawer/PdfPrintDrawer';
import ContractCreationDrawer from '@components/Project/ProjectContracts/ContractCreationDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const DocumentItemThumbStyle = styled.div`
    /* grid-column-start: 1;
    grid-column-end: 2;
    grid-row-start: 1;
    grid-row-end: 2; */
    svg {
        width: auto;
        height: 2.75rem;
    }
`;

const Wrapper = styled.div`
    margin-bottom: 16px;
    .ant-card-body-content {
        padding: 0 !important;
    }

    .modal-loading-container {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 1000;
    }

    .modal-loading-container-content {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: #fff;
        border-radius: 0.5rem;
        padding: 3rem 5rem;
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const GridCard = styled(Card.Grid)`
    float: left; //added because ant removed it to use flex- to update later
    width: 25%;
    height: ${(props) => (props.vertical ? 'auto' : '8rem')};
    @media only screen and (max-width: 1600px) {
        width: 33.33%;
    }
    @media only screen and (max-width: 1200px) {
        width: 50%;
    }
    @media only screen and (max-width: 600px) {
        width: 100%;
    }
    width: ${(props) => props.vertical && '100% !important'};

    .ant-list-item-meta {
        width: 100%;
    }
`;

const ListItemMeta = styled(List.Item.Meta)`
    padding: 0;
    cursor: pointer;
    align-items: center;

    &:hover {
        background-color: ${(props) => !props.$disablehover && props.theme.SelectedBackgroundColor};
        .document-image {
            color: ${(props) => !props.$disablehover && props.theme.PrimaryColor};
        }
    }
    .description-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .title-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;

const ConfirmedLabel = styled.div`
    color: ${(props) => props.theme.DangerColor};
`;

const ProjectContracts = ({ vertical }) => {
    const { projectId } = useProjectContext();
    const projectDispatch = useProjectContextDispatch();
    const [loading, setLoading] = useState(false);
    const [contracts, setContracts] = useState([]);
    const breakpoints = useNwBreakPoints();
    const [addContract, setAddContract] = useState(false);
    const [editContractDrawer, setEditContractDrawer] = useState(false);
    const [selectedContract, setSelectedContract] = useState();
    const [previewUrl, setPreviewUrl] = useState(null);
    const [generatePdfLoading, setGeneratePdfLoading] = useState(false);

    const fetchContracts = async () => {
        try {
            const contracts = await Axios.post('/docVersions/getFiltered', {
                ProjectID: projectId,
                Area: 'ProjectContract',
            });
            setContracts(contracts);
            setLoading(false);
        } catch (error) {
            console.error('fetch contracts error => ', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        if (projectId) {
            fetchContracts();
        }
    }, [projectId]);

    const onDeleteContract = async (contract) => {
        try {
            await Axios.delete(`/docVersions/${contract.ID}`);
            // Reload project data in context
            const updatedProject = await forceReloadProject(projectId);
            projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
            fetchContracts();
        } catch (error) {
            console.error('Errore', error);
        }
    };

    const getDescription = (contract) => {
        return (
            <div onClick={() => handleContractPreview(contract)}>
                <CreatedBy object={contract} short textonly />
                {contract.Confirmed && <ConfirmedLabel>Confirmed</ConfirmedLabel>}
            </div>
        );
    };

    const onOpenEditContractDrawer = (contract) => {
        setSelectedContract(contract);
        setEditContractDrawer(true);
    };

    const onCloseEditContractDrawer = async (contract) => {
        setSelectedContract(null);
        setEditContractDrawer(false);
        setLoading(true);
        // Reload project data in context
        const updatedProject = await forceReloadProject(projectId);
        projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
        fetchContracts();
    };

    const handleAddContract = () => {
        setAddContract(true);
    };

    const handleCloseContractDrawer = async () => {
        setAddContract(false);
        setLoading(true);
        // Reload project data in context
        const updatedProject = await forceReloadProject(projectId);
        projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
        fetchContracts();
    };

    const handleContractPreview = async (document) => {
        setGeneratePdfLoading(true);
        try {
            const url = await Axios.get(`/docVersions/pdf/${document.DocGuid}`);
            const documentUrl = new URL(url);
            setGeneratePdfLoading(false);
            setPreviewUrl(documentUrl.href);
        } catch (error) {
            setGeneratePdfLoading(false);
            notification.error({
                message: 'Error',
                description: 'Error loading contract preview',
            });
        }
    };

    const actions = (contract) => {
        return (
            <div>
                <NwDropDownMenu
                    actions={[
                        {
                            name: '',
                            key: 'edit',
                            pinned: true,
                            icon: byPrefixAndName.fasl['pen'],
                            action: () => onOpenEditContractDrawer(contract),
                        },
                        {
                            name: '',
                            type: 'delete',
                            key: 'delete',
                            pinned: true,
                            icon: byPrefixAndName.fasl['trash-alt'],
                            confirmationPlacement: 'left',
                            action: () => onDeleteContract(contract),
                        },
                    ]}
                />
            </div>
        );
    };

    return (
        <Wrapper>
            <NwCard title="Contracts" padding="no-padding" extra={<NwAddButton ghost onClick={handleAddContract} />}>
                {loading ? (
                    <Loading textBlack />
                ) : (
                    <>
                        {contracts.length > 0 ? (
                            <div>
                                {contracts.map((contract) => (
                                    <GridCard vertical={vertical} key={`contract-${contract.ID}`}>
                                        <ListItemMeta
                                            $disablehover
                                            avatar={
                                                <DocumentItemThumbStyle
                                                    className="document-image"
                                                    onClick={() => handleContractPreview(contract)}
                                                >
                                                    <NwIcon icon={byPrefixAndName.fasl['file-contract']} />
                                                </DocumentItemThumbStyle>
                                            }
                                            title={
                                                <div className="title-container">
                                                    <div
                                                        className="title"
                                                        onClick={() => handleContractPreview(contract)}
                                                    >
                                                        {contract.Area}
                                                    </div>
                                                    {actions && breakpoints !== 1 && actions(contract)}
                                                </div>
                                            }
                                            description={
                                                <div className="description-container">
                                                    <div className="description">{getDescription(contract)}</div>
                                                    {breakpoints === 1 && <div>{actions(contract)}</div>}
                                                </div>
                                            }
                                        />
                                    </GridCard>
                                ))}
                            </div>
                        ) : (
                            <EmptyState message="No contracts" />
                        )}
                    </>
                )}
                {addContract && <ContractCreationDrawer projectId={projectId} onClose={handleCloseContractDrawer} />}
                {editContractDrawer && (
                    <NwDrawer
                        onClose={onCloseEditContractDrawer}
                        padding="0px"
                        title="Edit Contract"
                        visible={true}
                        whiteBg={false}
                        width="80vw"
                        maxWidth={'1024px'}
                    >
                        <PdfPrintDrawer docVersion={selectedContract} saveButton onClose={onCloseEditContractDrawer} />
                    </NwDrawer>
                )}
                {previewUrl && (
                    <MediaDrawerPreviewOnly pdfPath={previewUrl} onCloseDrawer={() => setPreviewUrl(null)} />
                )}
                {generatePdfLoading && (
                    <div className="modal-loading-container">
                        <div className="modal-loading-container-content">
                            <Loading small text="Generating PDF" textBlack />
                        </div>
                    </div>
                )}
            </NwCard>
        </Wrapper>
    );
};

export default ProjectContracts;
